import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { fetchApplicationDetail } from "../../behavior/api/application";
import { Button } from "../../components/elements";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import type { InterviewDetailsProps } from "../../components/interview/InterviewDetails";
import InterviewDetails from "../../components/interview/InterviewDetails";
import HowToImproveAtInterviews from "../../components/interview/howToImproveAtInterviews/HowToImproveAtInterviews";
import InterviewNotFound from "../../components/interview/interviewNotFound/InterviewNotFound";
import ScriptFromInterview from "../../components/interview/scriptFromInterview/ScriptFromInterview";
import type { YourScoreProps } from "../../components/interview/yourScore/YourScore";
import YourScore from "../../components/interview/yourScore/YourScore";
import { AssessedMatch, PageRoute, ProfessionalExperience } from "../../constants";
import type { Feedback } from "../../types/application";
import type Application from "../../types/application";
import "./Interview.css";

const Interview = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const {
        data: application,
        isLoading: loading,
        error,
    } = useQuery<Application>({
        queryKey: ["application", id],
        queryFn: () => {
            if (!id) throw new Error("Application ID is required");
            return fetchApplicationDetail(id);
        },
        enabled: !!id,
    });

    const [interviewDetail, setInterviewDetail] = useState<InterviewDetailsProps>();
    const [interviewScore, setInterviewScore] = useState<YourScoreProps>({
        assessedMatch: AssessedMatch.OK_MATCH,
        professionalExperience: ProfessionalExperience.OK_MATCH,
        qualified: false,
    });
    const [interviewFeedback, setInterviewFeedback] = useState<Feedback[]>([]);
    const [interviewId, setInterviewId] = useState<string>("");

    useEffect(() => {
        if (!loading && application) {
            if (application.id) {
                if (application.job) {
                    const detail: InterviewDetailsProps = {
                        jobTitle: application.job.title,
                        company: application.job.company,
                        ...(application.completedAt && {
                            dateTime: application.completedAt,
                        }),
                    };

                    const scoreDetail: YourScoreProps = {
                        qualified: application.qualified,
                        assessedMatch: AssessedMatch[application.assessedMatch],
                        professionalExperience: ProfessionalExperience[application.professionalExperience],
                    };

                    if (application.interview) {
                        setInterviewId(application.interview.publicId);
                    }

                    setInterviewDetail(detail);
                    setInterviewScore(scoreDetail);
                    setInterviewFeedback(application.feedback);
                }
            }
        }
    }, [application, loading]);

    return (
        <>
            <Header type="LoggedIn" />
            {error || (!loading && !application?.id) ? (
                <InterviewNotFound />
            ) : (
                <div className="interview-wrapper">
                    <Button secondary link to={PageRoute.INTERVIEWS} className="back-button">
                        <ArrowLeft size={20} className="me-2 rtl:-scale-x-100" />
                        <div>{t("Interview.ButtonText_SeeAllInterviews")}</div>
                    </Button>
                    <InterviewDetails
                        jobTitle={interviewDetail?.jobTitle}
                        company={interviewDetail?.company}
                        dateTime={interviewDetail?.dateTime}
                        loading={loading}
                    />
                    <YourScore
                        professionalExperience={interviewScore.professionalExperience}
                        assessedMatch={interviewScore.assessedMatch}
                        qualified={interviewScore.qualified}
                        loading={loading}
                    />
                    {!loading && <ScriptFromInterview interviewId={interviewId} />}
                    <div className="gray-bg-wrapper">
                        <HowToImproveAtInterviews
                            scoreType={interviewScore.assessedMatch}
                            questions={interviewFeedback.length > 0}
                            feedbacks={interviewFeedback}
                            loading={loading}
                        />
                    </div>
                </div>
            )}
            <Footer />
        </>
    );
};

export default Interview;
